/**
 * Sistema de Temas — WhiteLabelClaw Design System
 * 
 * Permite trocar tema com 1 clique.
 * Compatível com CSS variables do shadcn/ui (tweakcn, etc).
 * 
 * USO:
 *   import { applyTheme, themes } from '../styles/themes';
 *   applyTheme('dark-purple');  // troca tudo
 *   getActiveTheme();           // retorna tema ativo
 * 
 * Temas importados do tweakcn (https://github.com/jnsahaj/tweakcn)
 * Apenas variantes dark — nosso app é dark theme.
 */

// ============================================================
// Tipo do tema — TODOS os tokens de cor/visual que podem mudar
// ============================================================
export interface ThemeColors {
  // Backgrounds
  bg: string;
  bgCard: string;
  bgCardHover: string;
  bgInput: string;
  bgInputHover: string;
  bgSecondary: string;
  bgOverlay: string;
  bgModal: string;

  // Borders
  border: string;
  borderHover: string;
  borderFocus: string;
  borderError: string;

  // Text
  text: string;
  textSecondary: string;
  textMuted: string;
  textDisabled: string;

  // Accent / Brand
  primary: string;
  primaryHover: string;
  success: string;
  successHover: string;
  warning: string;
  warningHover: string;
  danger: string;
  dangerHover: string;
  accent: string;
  accentHover: string;
  purple: string;
  purpleHover: string;

  // Status backgrounds — 3 níveis: subtle (hover), default (bg), strong (border/emphasis)
  primaryBgSubtle: string;
  primaryBg: string;
  primaryBgStrong: string;
  dangerBgSubtle: string;
  dangerBg: string;
  dangerBgStrong: string;
  successBgSubtle: string;
  successBg: string;
  successBgStrong: string;
  warningBgSubtle: string;
  warningBg: string;
  warningBgStrong: string;
  accentBgSubtle: string;
  accentBg: string;
  accentBgStrong: string;
  purpleBgSubtle: string;
  purpleBg: string;
  purpleBgStrong: string;

  // Background variants
  bgSubtle: string;      // muito sutil (hover leve)
  bgHover: string;       // hover genérico
  bgActive: string;      // active/selected

  // Border variants
  borderSubtle: string;  // borda muito sutil

  // Alpha colors
  whiteAlpha2: string;
  whiteAlpha3: string;
  whiteAlpha5: string;
  whiteAlpha8: string;
  whiteAlpha15: string;
  whiteAlpha30: string;
  blackAlpha15: string;
  blackAlpha30: string;
  blackAlpha40: string;
  blackAlpha50: string;

  // Shadows
  shadowSm: string;
  shadowMd: string;
  shadowLg: string;
  shadowXl: string;

  // Radius
  radiusSm: number;
  radiusMd: number;
  radiusLg: number;
  radiusXl: number;
  radiusFull: number;

  // Tipografia (temável)
  fontFamily: string;
  fontMono: string;
  fontDisplay: string;
  lineHeightBody: number;
  letterSpacingBody: string;
}

// ============================================================
// Helpers para derivar cores automaticamente
// ============================================================
function hexToRgb(hex: string): [number, number, number] {
  const h = hex.replace('#', '');
  return [
    parseInt(h.substring(0, 2), 16),
    parseInt(h.substring(2, 4), 16),
    parseInt(h.substring(4, 6), 16),
  ];
}

function rgbToHex(r: number, g: number, b: number): string {
  return '#' + [r, g, b].map(x => Math.max(0, Math.min(255, Math.round(x))).toString(16).padStart(2, '0')).join('');
}

/** Escurece uma cor hex em X% (0-100) */
function darken(hex: string, pct: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = 1 - pct / 100;
  return rgbToHex(r * f, g * f, b * f);
}

/** Clareia uma cor hex em X% (0-100) */
function lighten(hex: string, pct: number): string {
  const [r, g, b] = hexToRgb(hex);
  const f = pct / 100;
  return rgbToHex(r + (255 - r) * f, g + (255 - g) * f, b + (255 - b) * f);
}

/** Cria rgba() a partir de hex + alpha */
function withAlpha(hex: string, alpha: number): string {
  const [r, g, b] = hexToRgb(hex);
  return `rgba(${r},${g},${b},${alpha})`;
}

/** Converte radius string ("0.5rem") para number em px */
function radiusToNumber(r: string): number {
  if (r.endsWith('rem')) return Math.round(parseFloat(r) * 16);
  if (r.endsWith('px')) return Math.round(parseFloat(r));
  return Math.round(parseFloat(r));
}

/** Cria tema completo a partir de tokens parciais do shadcn dark */
function makeTheme(d: {
  bg: string;
  text: string;
  card: string;
  popover: string;
  primary: string;
  secondary: string;
  secondaryFg: string;
  muted: string;
  mutedFg: string;
  accent: string;
  destructive: string;
  border: string;
  ring: string;
  radius: string;
}): ThemeColors {
  const radiusBase = radiusToNumber(d.radius);
  return {
    bg: d.bg,
    bgCard: d.card,
    bgCardHover: lighten(d.card, 2),
    bgInput: d.muted,
    bgInputHover: lighten(d.muted, 2),
    bgSecondary: d.secondary,
    bgOverlay: 'rgba(0,0,0,0.6)',
    bgModal: d.popover,

    border: d.border,
    borderHover: lighten(d.border, 5),
    borderFocus: d.ring,
    borderError: d.destructive,

    text: d.text,
    textSecondary: d.secondaryFg,
    textMuted: d.mutedFg,
    textDisabled: darken(d.mutedFg, 20),

    primary: d.primary,
    primaryHover: darken(d.primary, 15),
    success: '#10B981',
    successHover: '#059669',
    warning: '#F59E0B',
    warningHover: '#D97706',
    danger: d.destructive,
    dangerHover: darken(d.destructive, 15),
    accent: d.accent,
    accentHover: darken(d.accent, 15),
    purple: d.accent,
    purpleHover: darken(d.accent, 15),

    primaryBgSubtle: withAlpha(d.primary, 0.06),
    primaryBg: withAlpha(d.primary, 0.12),
    primaryBgStrong: withAlpha(d.primary, 0.25),
    dangerBgSubtle: withAlpha(d.destructive, 0.06),
    dangerBg: withAlpha(d.destructive, 0.12),
    dangerBgStrong: withAlpha(d.destructive, 0.25),
    successBgSubtle: 'rgba(16,185,129,0.06)',
    successBg: 'rgba(16,185,129,0.12)',
    successBgStrong: 'rgba(16,185,129,0.25)',
    warningBgSubtle: 'rgba(245,158,11,0.06)',
    warningBg: 'rgba(245,158,11,0.12)',
    warningBgStrong: 'rgba(245,158,11,0.25)',
    accentBgSubtle: withAlpha(d.accent, 0.06),
    accentBg: withAlpha(d.accent, 0.12),
    accentBgStrong: withAlpha(d.accent, 0.25),
    purpleBgSubtle: withAlpha(d.accent, 0.06),
    purpleBg: withAlpha(d.accent, 0.12),
    purpleBgStrong: withAlpha(d.accent, 0.25),

    bgSubtle: 'rgba(255,255,255,0.02)',
    bgHover: 'rgba(255,255,255,0.08)',
    bgActive: 'rgba(255,255,255,0.1)',

    borderSubtle: 'rgba(255,255,255,0.04)',

    shadowSm: '0 1px 2px rgba(0,0,0,0.3)',
    shadowMd: '0 4px 6px rgba(0,0,0,0.3)',
    shadowLg: '0 10px 15px rgba(0,0,0,0.3)',
    shadowXl: '0 20px 25px rgba(0,0,0,0.3)',

    radiusSm: Math.max(2, radiusBase - 4),
    radiusMd: radiusBase,
    radiusLg: radiusBase + 4,
    radiusXl: radiusBase + 8,
    radiusFull: 9999,

    fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
    fontMono: "'SF Mono', 'Fira Code', 'Consolas', monospace",
    fontDisplay: "'Inter', system-ui, -apple-system, sans-serif",
    lineHeightBody: 1.6,
    letterSpacingBody: '0',
  };
}

// ============================================================
// Tema padrão (atual do WhiteLabelClaw — dark)
// ============================================================
export const defaultTheme: ThemeColors = {
  bg: '#212121',
  bgCard: 'rgba(255,255,255,0.03)',
  bgCardHover: 'rgba(255,255,255,0.05)',
  bgInput: 'rgba(255,255,255,0.05)',
  bgInputHover: 'rgba(255,255,255,0.07)',
  bgSecondary: '#171717',
  bgOverlay: 'rgba(0,0,0,0.6)',
  bgModal: '#2A2A2A',

  border: 'rgba(255,255,255,0.06)',
  borderHover: 'rgba(255,255,255,0.1)',
  borderFocus: 'rgba(59,130,246,0.5)',
  borderError: '#EF4444',

  text: '#E8E8F0',
  textSecondary: '#9CA3AF',
  textMuted: '#6B7280',
  textDisabled: '#4B5563',

  primary: '#3B82F6',
  primaryHover: '#2563EB',
  success: '#10B981',
  successHover: '#059669',
  warning: '#F59E0B',
  warningHover: '#D97706',
  danger: '#EF4444',
  dangerHover: '#DC2626',
  accent: '#6D4CFF',
  accentHover: '#5A3DE6',
  purple: '#8B5CF6',
  purpleHover: '#7C3AED',

  primaryBgSubtle: 'rgba(59,130,246,0.06)',
  primaryBg: 'rgba(59,130,246,0.12)',
  primaryBgStrong: 'rgba(59,130,246,0.25)',
  dangerBgSubtle: 'rgba(239,68,68,0.06)',
  dangerBg: 'rgba(239,68,68,0.12)',
  dangerBgStrong: 'rgba(239,68,68,0.25)',
  successBgSubtle: 'rgba(16,185,129,0.06)',
  successBg: 'rgba(16,185,129,0.12)',
  successBgStrong: 'rgba(16,185,129,0.25)',
  warningBgSubtle: 'rgba(245,158,11,0.06)',
  warningBg: 'rgba(245,158,11,0.12)',
  warningBgStrong: 'rgba(245,158,11,0.25)',
  accentBgSubtle: 'rgba(109,76,255,0.06)',
  accentBg: 'rgba(109,76,255,0.12)',
  accentBgStrong: 'rgba(109,76,255,0.25)',
  purpleBgSubtle: 'rgba(139,92,246,0.06)',
  purpleBg: 'rgba(139,92,246,0.12)',
  purpleBgStrong: 'rgba(139,92,246,0.25)',

  bgSubtle: 'rgba(255,255,255,0.02)',
  bgHover: 'rgba(255,255,255,0.08)',
  bgActive: 'rgba(255,255,255,0.1)',

  borderSubtle: 'rgba(255,255,255,0.04)',

  whiteAlpha2: 'rgba(255,255,255,0.02)',
  whiteAlpha3: 'rgba(255,255,255,0.03)',
  whiteAlpha5: 'rgba(255,255,255,0.05)',
  whiteAlpha8: 'rgba(255,255,255,0.08)',
  whiteAlpha15: 'rgba(255,255,255,0.15)',
  whiteAlpha30: 'rgba(255,255,255,0.3)',
  blackAlpha15: 'rgba(0,0,0,0.15)',
  blackAlpha30: 'rgba(0,0,0,0.3)',
  blackAlpha40: 'rgba(0,0,0,0.4)',
  blackAlpha50: 'rgba(0,0,0,0.5)',

  shadowSm: '0 1px 2px rgba(0,0,0,0.3)',
  shadowMd: '0 4px 6px rgba(0,0,0,0.3)',
  shadowLg: '0 10px 15px rgba(0,0,0,0.3)',
  shadowXl: '0 20px 25px rgba(0,0,0,0.3)',

  radiusSm: 4,
  radiusMd: 8,
  radiusLg: 12,
  radiusXl: 16,
  radiusFull: 9999,

  fontFamily: "'Inter', system-ui, -apple-system, sans-serif",
  fontMono: "'SF Mono', 'Fira Code', 'Consolas', monospace",
  fontDisplay: "'Inter', system-ui, -apple-system, sans-serif",
  lineHeightBody: 1.6,
  letterSpacingBody: '0',
};

// ============================================================
// Labels em português para cada tema
// ============================================================
export const themeLabels: Record<string, string> = {
  'default': 'WhiteLabelClaw v1',
  'modern-minimal': 'Minimalista Moderno',
  'violet-bloom': 'Violeta Florido',
  't3-chat': 'T3 Chat',
  'twitter': 'Twitter / X',
  'mocha-mousse': 'Mocha Mousse',
  'bubblegum': 'Chiclete',
  'amethyst-haze': 'Névoa Ametista',
  'notebook': 'Caderno',
  'doom-64': 'Doom 64',
  'catppuccin': 'Catppuccin',
  'graphite': 'Grafite',
  'perpetuity': 'Perpetuidade',
  'kodama-grove': 'Bosque Kodama',
  'cosmic-night': 'Noite Cósmica',
  'tangerine': 'Tangerina',
  'quantum-rose': 'Rosa Quântica',
  'nature': 'Natureza',
  'bold-tech': 'Tech Vibrante',
};

// ============================================================
// Temas pré-definidos (18 do tweakcn + default)
// ============================================================
export const themes: Record<string, ThemeColors> = {
  'default': defaultTheme,

  'modern-minimal': makeTheme({
    bg: '#171717', text: '#e5e5e5', card: '#262626', popover: '#262626',
    primary: '#3b82f6', secondary: '#262626', secondaryFg: '#e5e5e5',
    muted: '#1f1f1f', mutedFg: '#a3a3a3', accent: '#1e3a8a',
    destructive: '#ef4444', border: '#404040', ring: '#3b82f6', radius: '0.375rem',
  }),

  'violet-bloom': makeTheme({
    bg: '#1a1b1e', text: '#f0f0f0', card: '#222327', popover: '#222327',
    primary: '#8c5cff', secondary: '#2a2c33', secondaryFg: '#f0f0f0',
    muted: '#2a2c33', mutedFg: '#a0a0a0', accent: '#1e293b',
    destructive: '#f87171', border: '#33353a', ring: '#8c5cff', radius: '1.4rem',
  }),

  't3-chat': makeTheme({
    bg: '#221d27', text: '#d2c4de', card: '#2c2632', popover: '#100a0e',
    primary: '#a3004c', secondary: '#362d3d', secondaryFg: '#d4c7e1',
    muted: '#28222d', mutedFg: '#c2b6cf', accent: '#463753',
    destructive: '#301015', border: '#3b3237', ring: '#db2777', radius: '0.5rem',
  }),

  'twitter': makeTheme({
    bg: '#000000', text: '#e7e9ea', card: '#17181c', popover: '#000000',
    primary: '#1c9cf0', secondary: '#f0f3f4', secondaryFg: '#0f1419',
    muted: '#181818', mutedFg: '#72767a', accent: '#061622',
    destructive: '#f4212e', border: '#242628', ring: '#1da1f2', radius: '1.3rem',
  }),

  'mocha-mousse': makeTheme({
    bg: '#2d2521', text: '#F1F0E5', card: '#3c332e', popover: '#3c332e',
    primary: '#C39E88', secondary: '#8A655A', secondaryFg: '#F1F0E5',
    muted: '#56453F', mutedFg: '#c5aa9b', accent: '#BAAB92',
    destructive: '#E57373', border: '#56453F', ring: '#C39E88', radius: '0.5rem',
  }),

  'bubblegum': makeTheme({
    bg: '#12242e', text: '#f3e3ea', card: '#1c2e38', popover: '#1c2e38',
    primary: '#fbe2a7', secondary: '#e4a2b1', secondaryFg: '#12242e',
    muted: '#24272b', mutedFg: '#e4a2b1', accent: '#c67b96',
    destructive: '#e35ea4', border: '#324859', ring: '#50afb6', radius: '0.4rem',
  }),

  'amethyst-haze': makeTheme({
    bg: '#1a1823', text: '#e0ddef', card: '#232030', popover: '#232030',
    primary: '#a995c9', secondary: '#5a5370', secondaryFg: '#e0ddef',
    muted: '#242031', mutedFg: '#a09aad', accent: '#372e3f',
    destructive: '#e57373', border: '#302c40', ring: '#a995c9', radius: '0.5rem',
  }),

  'notebook': makeTheme({
    bg: '#2b2b2b', text: '#dcdcdc', card: '#333333', popover: '#333333',
    primary: '#b0b0b0', secondary: '#5a5a5a', secondaryFg: '#c0c0c0',
    muted: '#454545', mutedFg: '#a0a0a0', accent: '#e0e0e0',
    destructive: '#d9afaf', border: '#4f4f4f', ring: '#c0c0c0', radius: '0.625rem',
  }),

  'doom-64': makeTheme({
    bg: '#1a1a1a', text: '#e0e0e0', card: '#2a2a2a', popover: '#2a2a2a',
    primary: '#e53935', secondary: '#689f38', secondaryFg: '#000000',
    muted: '#252525', mutedFg: '#a0a0a0', accent: '#64b5f6',
    destructive: '#ffa000', border: '#4a4a4a', ring: '#e53935', radius: '0px',
  }),

  'catppuccin': makeTheme({
    bg: '#181825', text: '#cdd6f4', card: '#1e1e2e', popover: '#45475a',
    primary: '#cba6f7', secondary: '#585b70', secondaryFg: '#cdd6f4',
    muted: '#292c3c', mutedFg: '#a6adc8', accent: '#89dceb',
    destructive: '#f38ba8', border: '#313244', ring: '#cba6f7', radius: '0.35rem',
  }),

  'graphite': makeTheme({
    bg: '#1a1a1a', text: '#d9d9d9', card: '#202020', popover: '#202020',
    primary: '#a0a0a0', secondary: '#303030', secondaryFg: '#d9d9d9',
    muted: '#2a2a2a', mutedFg: '#808080', accent: '#404040',
    destructive: '#e06666', border: '#353535', ring: '#a0a0a0', radius: '0.35rem',
  }),

  'perpetuity': makeTheme({
    bg: '#0a1a20', text: '#4de8e8', card: '#0c2025', popover: '#0c2025',
    primary: '#4de8e8', secondary: '#164955', secondaryFg: '#4de8e8',
    muted: '#0f3039', mutedFg: '#36a5a5', accent: '#164955',
    destructive: '#e83c3c', border: '#164955', ring: '#4de8e8', radius: '0.125rem',
  }),

  'kodama-grove': makeTheme({
    bg: '#3a3529', text: '#ede4d4', card: '#413c33', popover: '#413c33',
    primary: '#8a9f7b', secondary: '#5a5345', secondaryFg: '#ede4d4',
    muted: '#4a4439', mutedFg: '#a8a096', accent: '#a18f5c',
    destructive: '#b5766a', border: '#5a5345', ring: '#8a9f7b', radius: '0.425rem',
  }),

  'cosmic-night': makeTheme({
    bg: '#0f0f1a', text: '#e2e2f5', card: '#1a1a2e', popover: '#1a1a2e',
    primary: '#a48fff', secondary: '#2d2b55', secondaryFg: '#c4c2ff',
    muted: '#222244', mutedFg: '#a0a0c0', accent: '#303060',
    destructive: '#ff5470', border: '#303052', ring: '#a48fff', radius: '0.5rem',
  }),

  'tangerine': makeTheme({
    bg: '#1c2433', text: '#e5e5e5', card: '#2a3040', popover: '#262b38',
    primary: '#e05d38', secondary: '#2a303e', secondaryFg: '#e5e5e5',
    muted: '#2a303e', mutedFg: '#a3a3a3', accent: '#2a3656',
    destructive: '#ef4444', border: '#3d4354', ring: '#e05d38', radius: '0.75rem',
  }),

  'quantum-rose': makeTheme({
    bg: '#1a0922', text: '#ffb3ff', card: '#2a1435', popover: '#2a1435',
    primary: '#ff6bef', secondary: '#46204f', secondaryFg: '#ffb3ff',
    muted: '#331941', mutedFg: '#d67ad6', accent: '#5a1f5d',
    destructive: '#ff2876', border: '#4a1b5f', ring: '#ff6bef', radius: '0.5rem',
  }),

  'nature': makeTheme({
    bg: '#1c2a1f', text: '#f0ebe5', card: '#2d3a2e', popover: '#2d3a2e',
    primary: '#4caf50', secondary: '#3e4a3d', secondaryFg: '#d7e0d6',
    muted: '#252f26', mutedFg: '#d7cfc4', accent: '#388e3c',
    destructive: '#c62828', border: '#3e4a3d', ring: '#4caf50', radius: '0.5rem',
  }),

  'bold-tech': makeTheme({
    bg: '#0f172a', text: '#e0e7ff', card: '#1e1b4b', popover: '#1e1b4b',
    primary: '#8b5cf6', secondary: '#1e1b4b', secondaryFg: '#e0e7ff',
    muted: '#171447', mutedFg: '#c4b5fd', accent: '#4338ca',
    destructive: '#ef4444', border: '#2e1065', ring: '#8b5cf6', radius: '0.625rem',
  }),
};

// ============================================================
// Overrides de tipografia por tema
// Cada tema pode ter sua própria família de fontes
// ============================================================
themes['notebook'].fontFamily = "'Courier New', 'Courier', monospace";
themes['notebook'].fontMono = "'Courier New', 'Courier', monospace";
themes['notebook'].fontDisplay = "'Courier New', 'Courier', monospace";
themes['notebook'].lineHeightBody = 1.8;
themes['notebook'].letterSpacingBody = '0.5px';

themes['doom-64'].fontFamily = "'Share Tech Mono', 'Fira Code', monospace";
themes['doom-64'].fontMono = "'Share Tech Mono', 'Fira Code', monospace";
themes['doom-64'].fontDisplay = "'Share Tech Mono', monospace";
themes['doom-64'].letterSpacingBody = '1px';

themes['perpetuity'].fontFamily = "'JetBrains Mono', 'Fira Code', monospace";
themes['perpetuity'].fontMono = "'JetBrains Mono', monospace";
themes['perpetuity'].letterSpacingBody = '0.5px';

themes['catppuccin'].fontFamily = "'Nunito', 'Inter', system-ui, sans-serif";
themes['catppuccin'].fontDisplay = "'Nunito', 'Inter', sans-serif";

themes['twitter'].fontFamily = "'Chirp', 'Inter', system-ui, sans-serif";
themes['twitter'].fontDisplay = "'Chirp', 'Inter', sans-serif";

themes['modern-minimal'].fontFamily = "'Geist', 'Inter', system-ui, sans-serif";
themes['modern-minimal'].fontDisplay = "'Geist', 'Inter', sans-serif";

themes['bold-tech'].fontFamily = "'Space Grotesk', 'Inter', system-ui, sans-serif";
themes['bold-tech'].fontDisplay = "'Space Grotesk', 'Inter', sans-serif";

// ============================================================
// Google Fonts — carregamento automático por tema
// ============================================================
const THEME_FONTS: Record<string, string> = {
  'doom-64': 'Share+Tech+Mono:wght@400',
  'perpetuity': 'JetBrains+Mono:wght@400;500;600;700',
  'catppuccin': 'Nunito:wght@400;500;600;700',
  'modern-minimal': 'Geist:wght@400;500;600;700',
  'bold-tech': 'Space+Grotesk:wght@400;500;600;700',
};

let _loadedFont: string | null = null;

/** Carrega Google Font pro tema ativo (se necessário) */
export function loadThemeFont(themeId: string): void {
  if (typeof document === 'undefined') return;
  const fontSpec = THEME_FONTS[themeId];
  if (!fontSpec || fontSpec === _loadedFont) return;

  // Remove link anterior se existir
  const existing = document.getElementById('wlc-theme-font');
  if (existing) existing.remove();

  const link = document.createElement('link');
  link.id = 'wlc-theme-font';
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontSpec}&display=swap`;
  document.head.appendChild(link);
  _loadedFont = fontSpec;
}

// ============================================================
// Mapeamento shadcn/ui CSS vars → nossos tokens
// Usado pelo conversor de temas do tweakcn
// ============================================================
export const SHADCN_TO_TOKENS: Record<string, keyof ThemeColors> = {
  '--background': 'bg',
  '--card': 'bgCard',
  '--popover': 'bgModal',
  '--primary': 'primary',
  '--secondary': 'bgSecondary',
  '--muted': 'bgInput',
  '--muted-foreground': 'textMuted',
  '--accent': 'accent',
  '--destructive': 'danger',
  '--border': 'border',
  '--input': 'bgInput',
  '--ring': 'borderFocus',
  '--foreground': 'text',
  '--card-foreground': 'text',
  '--popover-foreground': 'text',
  '--primary-foreground': 'text',
  '--secondary-foreground': 'textSecondary',
  '--accent-foreground': 'text',
  '--destructive-foreground': 'text',
};

// ============================================================
// Conversor oklch → hex
// ============================================================
function oklchToHex(l: number, c: number, h: number): string {
  const hRad = (h * Math.PI) / 180;
  const a = c * Math.cos(hRad);
  const b = c * Math.sin(hRad);

  const l_ = l + 0.3963377774 * a + 0.2158037573 * b;
  const m_ = l - 0.1055613458 * a - 0.0638541728 * b;
  const s_ = l - 0.0894841775 * a - 1.2914855480 * b;

  const lc = l_ * l_ * l_;
  const mc = m_ * m_ * m_;
  const sc = s_ * s_ * s_;

  const r = +4.0767416621 * lc - 3.3077115913 * mc + 0.2309699292 * sc;
  const g = -1.2684380046 * lc + 2.6097574011 * mc - 0.3413193965 * sc;
  const bl = -0.0041960863 * lc - 0.7034186147 * mc + 1.7076147010 * sc;

  const toSRGB = (x: number) => {
    const clamped = Math.max(0, Math.min(1, x));
    return clamped <= 0.0031308
      ? Math.round(clamped * 12.92 * 255)
      : Math.round((1.055 * Math.pow(clamped, 1 / 2.4) - 0.055) * 255);
  };

  const rr = toSRGB(r).toString(16).padStart(2, '0');
  const gg = toSRGB(g).toString(16).padStart(2, '0');
  const bb = toSRGB(bl).toString(16).padStart(2, '0');

  return `#${rr}${gg}${bb}`;
}

// ============================================================
// Parser de CSS variables do shadcn/tweakcn
// ============================================================
export function parseShadcnCSS(css: string): Partial<ThemeColors> {
  const result: Partial<ThemeColors> = {};
  const lines = css.split('\n');

  for (const line of lines) {
    const match = line.match(/^\s*(--[\w-]+)\s*:\s*(.+?)\s*;?\s*$/);
    if (!match) continue;

    const [, varName, value] = match;
    const tokenKey = SHADCN_TO_TOKENS[varName];
    if (!tokenKey) continue;

    let hex = value.trim();
    const oklchMatch = hex.match(/oklch\(\s*([\d.]+)\s+([\d.]+)\s+([\d.]+)\s*(?:\/\s*([\d.]+%?))?\s*\)/);
    if (oklchMatch) {
      const [, l, c, h, alpha] = oklchMatch;
      hex = oklchToHex(parseFloat(l), parseFloat(c), parseFloat(h));
      if (alpha) {
        const a = alpha.endsWith('%') ? parseFloat(alpha) / 100 : parseFloat(alpha);
        if (a < 1) {
          const r = parseInt(hex.slice(1, 3), 16);
          const g = parseInt(hex.slice(3, 5), 16);
          const b = parseInt(hex.slice(5, 7), 16);
          hex = `rgba(${r},${g},${b},${a})`;
        }
      }
    }

    (result as any)[tokenKey] = hex;
  }

  return result;
}

// ============================================================
// Criar tema completo a partir de CSS do shadcn
// ============================================================
export function createThemeFromShadcn(css: string, name: string): ThemeColors {
  const overrides = parseShadcnCSS(css);
  return { ...defaultTheme, ...overrides };
}

// ============================================================
// Gerenciador de tema ativo (runtime)
// ============================================================
const STORAGE_KEY = 'wlc-theme';
let activeThemeId = 'default';
let customThemes: Record<string, ThemeColors> = {};

export function getActiveThemeId(): string {
  if (typeof window !== 'undefined') {
    return localStorage.getItem(STORAGE_KEY) || 'default';
  }
  return activeThemeId;
}

export function getActiveTheme(): ThemeColors {
  const id = getActiveThemeId();
  return themes[id] || customThemes[id] || defaultTheme;
}

export function getAllThemes(): { id: string; name: string; colors: ThemeColors }[] {
  const builtIn = Object.entries(themes).map(([id, colors]) => ({
    id,
    name: themeLabels[id] || id.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' '),
    colors,
  }));
  const custom = Object.entries(customThemes).map(([id, colors]) => ({
    id,
    name: id.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' '),
    colors,
  }));
  return [...builtIn, ...custom];
}

export function registerTheme(id: string, colors: ThemeColors): void {
  customThemes[id] = colors;
  if (typeof window !== 'undefined') {
    localStorage.setItem('wlc-custom-themes', JSON.stringify(customThemes));
  }
}

export function loadCustomThemes(): void {
  if (typeof window !== 'undefined') {
    try {
      const saved = localStorage.getItem('wlc-custom-themes');
      if (saved) customThemes = JSON.parse(saved);
    } catch { /* ignorar */ }
  }
}

export function applyTheme(themeId: string): boolean {
  const theme = themes[themeId] || customThemes[themeId];
  if (!theme) return false;

  activeThemeId = themeId;
  if (typeof window !== 'undefined') {
    localStorage.setItem(STORAGE_KEY, themeId);

    // Atualiza CSS custom properties no :root — tudo muda instantâneo, sem reload
    // Import dinâmico pra evitar circular dependency
    import('./tokens').then(({ applyThemeCSS }) => {
      applyThemeCSS(theme);
    });

    // Carrega Google Font do tema (se necessário)
    loadThemeFont(themeId);

    window.dispatchEvent(new CustomEvent('theme-change', { detail: { themeId, theme } }));
  }

  return true;
}
